import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Home, Layers, Droplets, HelpCircle, Loader2, ShieldCheck, Star, Phone } from "lucide-react";
import { BIZ, IMG } from "../data/site";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = [
  { v: "Roofing", i: Home }, { v: "Siding", i: Layers }, { v: "Gutters", i: Droplets }, { v: "Not sure yet", i: HelpCircle },
];
const TYPES = {
  Roofing: ["Full roof replacement", "Roof repair / leak", "Storm or hail damage", "Free drone inspection"],
  Siding: ["Full siding replacement", "Siding repair", "Soffit & fascia", "Not sure yet"],
  Gutters: ["New seamless gutters", "Gutter guards", "Gutter repair", "Not sure yet"],
  "Not sure yet": ["Free inspection", "Storm or hail damage", "Full exterior makeover", "Something else"],
};
const TIMELINES = ["As soon as possible", "Within 1–3 months", "3+ months", "Just planning"];
const OWNER = ["Yes, I own it", "No / buying soon"];
const TITLES = ["Where is your project?", "What do you need help with?", "What kind of project?", "When do you want to start?", "Do you own the home?", "Last step — where should we send your quote?"];

const Choice = ({ label, Icon, onClick, active, id }) => (
  <button type="button" onClick={onClick} data-testid={`quote-choice-${id}`}
    className={`flex w-full items-center gap-4 rounded-xl border-2 p-4 text-left font-semibold transition-[border-color,background-color] duration-200 ${active ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-900"}`}>
    {Icon && <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-slate-900 text-white"><Icon className="h-5 w-5" /></span>}
    <span className="flex-1 text-slate-900">{label}</span>
    <ArrowRight className="h-4 w-4 text-slate-400" />
  </button>
);

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function GetQuote() {
  const [params] = useSearchParams();
  const initZip = (params.get("zip") || "").replace(/\D/g, "").slice(0, 5);
  const [step, setStep] = useState(initZip.length === 5 ? 1 : 0);
  const [d, setD] = useState({ zip: initZip, service: "", project_type: "", timeline: "", homeowner: "", name: "", phone: "", email: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const pick = (k, v) => { setD({ ...d, [k]: v }); setStep(step + 1); };
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/estimates`, { ...d, email: d.email || null, address: d.address ? `${d.address} ${d.zip}` : d.zip, page: "/get-quote" });
      setDone(true);
      toast.success("Quote request sent!");
    } catch {
      toast.error(`Please check your info or call ${BIZ.phone}.`);
    } finally { setBusy(false); }
  };

  const steps = [
    <form key="zip" onSubmit={(e) => { e.preventDefault(); if (d.zip.length === 5) setStep(1); }} className="grid gap-3">
      <input inputMode="numeric" autoComplete="postal-code" autoFocus maxLength={5} value={d.zip} onChange={(e) => setD({ ...d, zip: e.target.value.replace(/\D/g, "") })} placeholder="Enter ZIP code" className="inp py-4 text-center text-2xl font-bold tracking-[0.3em]" data-testid="quote-zip-input" />
      <button disabled={d.zip.length !== 5} className="btn-amber justify-center py-4 text-base disabled:opacity-50" data-testid="quote-zip-next">Continue <ArrowRight className="h-4 w-4" /></button>
    </form>,
    <div key="svc" className="grid gap-3">{SERVICES.map((s) => <Choice key={s.v} label={s.v} Icon={s.i} id={slug(s.v)} active={d.service === s.v} onClick={() => setD({ ...d, service: s.v, project_type: "" }) || setStep(2)} />)}</div>,
    <div key="type" className="grid gap-3">{(TYPES[d.service] || []).map((t) => <Choice key={t} label={t} id={slug(t)} active={d.project_type === t} onClick={() => pick("project_type", t)} />)}</div>,
    <div key="time" className="grid gap-3">{TIMELINES.map((t) => <Choice key={t} label={t} id={slug(t)} active={d.timeline === t} onClick={() => pick("timeline", t)} />)}</div>,
    <div key="own" className="grid gap-3">{OWNER.map((t) => <Choice key={t} label={t} id={slug(t)} active={d.homeowner === t} onClick={() => pick("homeowner", t)} />)}</div>,
    <form key="contact" onSubmit={submit} className="grid gap-3">
      <input required minLength={2} autoComplete="name" value={d.name} onChange={set("name")} placeholder="Full name" className="inp" data-testid="quote-name-input" />
      <input required minLength={7} type="tel" autoComplete="tel" value={d.phone} onChange={set("phone")} placeholder="Phone number" className="inp" data-testid="quote-phone-input" />
      <input type="email" autoComplete="email" value={d.email} onChange={set("email")} placeholder="Email (optional)" className="inp" data-testid="quote-email-input" />
      <input autoComplete="street-address" value={d.address} onChange={set("address")} placeholder="Street address (optional)" className="inp" data-testid="quote-address-input" />
      <button disabled={busy} className="btn-amber justify-center py-4 text-base" data-testid="quote-submit-button">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Get my free quote <ArrowRight className="h-4 w-4" /></>}</button>
      <p className="text-center text-xs text-slate-500">No obligation. We'll only use your info to contact you about this project.</p>
    </form>,
  ];

  return (
    <main className="min-h-[80vh] bg-slate-100 lg:grid lg:grid-cols-2" data-testid="get-quote-page">
      <Seo title="Free Roofing, Siding & Gutter Quote | Good Home Improvement" description="Get a free roof, siding or gutter estimate in Milwaukee and Waukesha County. Answer a few quick questions or call 414-396-7510. Free drone roof inspection." path="/get-quote" />
      <div className="relative hidden overflow-hidden lg:block">
        <img src={IMG.crewTeam} alt="Good Home Improvement crew" className="kenburns absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/30 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white">
          <p className="font-display text-5xl font-black uppercase leading-[0.9]">Free quotes from a local Milwaukee pro</p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-200">
            <span className="flex items-center gap-2"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />5.0 on Google</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-400" />BBB A rating</span>
            <span>{BIZ.license}</span>
          </div>
        </div>
      </div>
      <div className="flex items-start justify-center px-4 py-10 md:py-16">
        <div className="w-full max-w-md">
          <div className="mb-5 flex items-center justify-center gap-3 text-xs text-slate-600 lg:hidden">
            <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-slate-900 text-slate-900" />5.0 Google</span>·<span>BBB A</span>·<span>Licensed</span>
          </div>
          <div className="rounded-2xl bg-white p-6 shadow-xl md:p-8" data-testid="quote-wizard">
            {done ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="py-8 text-center" data-testid="quote-success">
                <CheckCircle2 className="mx-auto h-14 w-14 text-slate-900" />
                <h1 className="font-display mt-4 text-3xl font-black uppercase">You're all set, {d.name.split(" ")[0]}!</h1>
                <p className="mt-3 text-slate-600">We received your {d.service.toLowerCase()} request and will call you soon to schedule your free estimate.</p>
                <a href={BIZ.tel} className="btn-amber mt-6" data-testid="quote-success-call"><Phone className="h-4 w-4" />Call now: {BIZ.phone}</a>
              </motion.div>
            ) : (
              <>
                <div className="mb-6 flex items-center justify-between">
                  {step > 0 ? <button onClick={() => setStep(step - 1)} className="flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="quote-back-button"><ArrowLeft className="h-4 w-4" />Back</button> : <span />}
                  <span className="text-xs font-bold uppercase tracking-widest text-slate-500" data-testid="quote-step-indicator">Step {step + 1} of 6</span>
                </div>
                <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-slate-100"><motion.div className="h-full bg-slate-900" animate={{ width: `${((step + 1) / 6) * 100}%` }} transition={{ duration: 0.4 }} /></div>
                <h1 className="font-display mb-6 text-3xl font-black uppercase leading-none text-slate-900" data-testid="quote-step-title">{TITLES[step]}</h1>
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>{steps[step]}</motion.div>
              </>
            )}
          </div>
          <p className="mt-5 text-center text-sm text-slate-600">Rather talk? <a href={BIZ.tel} className="font-bold text-slate-900" data-testid="quote-call-link">{BIZ.phone}</a></p>
        </div>
      </div>
    </main>
  );
}
